// import arrayMove from 'array-move';
// import {
//   SortableContainer,
//   SortableElement,
//   SortableHandle,
// } from 'react-sortable-hoc';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import { nanoid } from 'nanoid';
import { CloseCircleFilled } from '@ant-design/icons';
import alert from '@components/alert';
import './styles.scss';

// Replacement for SortableHandle using @dnd-kit
export const DragHandle = ({ listeners, attributes }) => (
  <div
    className="drag-handle"
    {...listeners}
    {...attributes}
    style={{ cursor: 'grab' }}
  >
    <span className="appkiticon icon-menu-outline" />
  </div>
);

// Replacement for SortableElement using @dnd-kit
const SortableItem = ({ value, handleInput, index, handleRemove, i }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: value.identifier || i.toString(),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li ref={setNodeRef} style={style}>
      <div className="drag-item">
        <DragHandle listeners={listeners} attributes={attributes} />
        {value.userAdded ? (
          <>
            <Input
              value={value.sectionTitle}
              onChange={(e) => handleInput(e, value, i)}
              maxLength={40}
            />
            <CloseCircleFilled
              className="dynamic-delete-button"
              onClick={handleRemove(i)}
            />
          </>
        ) : (
          <div className="name">{value.sectionTitle}</div>
        )}
      </div>
      <div className="error ant-form-item-explain-error">
        {value.hasError && value.hasError}
      </div>
    </li>
  );
};

// Replacement for SortableContainer using @dnd-kit
const SortableList = ({ items, ...rest }) => (
  <ul className="sort-list">
    {items.map((value, index) => (
      <SortableItem
        key={`item-${value.identifier || index}`}
        value={value}
        i={index}
        index={index}
        {...rest}
      />
    ))}
  </ul>
);

const CategoryModal = ({ onOk, onCancel, data = [], ...restProps }) => {
  const [order, setOrder] = useState([]);
  const [canSave, setCanSave] = useState(true);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 20,
      },
    }),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    const hasError = order.find((item) => item.hasError);
    setCanSave(!hasError);
  }, [order]);

  const removeOrder = (i) => {
    const newOrder = [...order];
    newOrder.splice(i, 1);
    setOrder(newOrder);
  };

  const handleRemove = (i) => () => {
    alert.confirmWithSave({
      onOk() {
        removeOrder(i);
      },
      onCancel(close) {
        typeof close === 'function' && close();
      },
      cancelText: 'Cancel',
      okText: 'yes',
      content: 'Do you want to delete the category and its elements?',
    });
  };

  const handleInput = (e, value, index) => {
    const name = e.target.value;
    order[index] = validate({ ...value, sectionTitle: name }, order);
    setOrder([...order]);
  };

  const validate = useCallback((orderItem, order) => {
    const hasRepeatName = order
      .filter((i) => i.identifier !== orderItem.identifier)
      .find((i) => i.sectionTitle === orderItem.sectionTitle);
    if (!orderItem.sectionTitle || !orderItem.sectionTitle.trim()) {
      orderItem.hasError = `* The title is required`;
    } else if (hasRepeatName) {
      orderItem.hasError = `* The ${orderItem.sectionTitle} already exists, please enter another name.`;
    } else {
      orderItem.hasError = false;
    }

    return orderItem;
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = order.findIndex(
        (item, index) => (item.identifier || index.toString()) === active.id,
      );
      const newIndex = order.findIndex(
        (item, index) => (item.identifier || index.toString()) === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        setOrder((currentOrder) => arrayMove(currentOrder, oldIndex, newIndex));
      }
    }
  };

  const handleOk = async () => {
    setLoading(true);

    const validatedOrder = order.map((i) => validate(i, order));
    if (validatedOrder.some((i) => i.hasError)) {
      setOrder(validatedOrder);
      setLoading(false);
      return;
    }

    const res = order.map((item, i) => {
      Reflect.deleteProperty(item, 'hasError');
      return item;
    });
    await onOk(res);
    setLoading(false);
  };

  const handleCancel = () => {
    onCancel();
    resetForm();
  };

  const handleAdd = () => {
    const newOrder = genNewOrder(order.length);
    setOrder([...order, newOrder]);
  };

  const genNewOrder = (index) =>
    // const orderName = `${NEW_ORDER_TEXT}${!index ? '' : `(${index})`}`;
    ({
      userAdded: true,
      identifier: nanoid(),
      isNewAdded: true,
      sectionTitle: '',
    });

  const resetForm = () => {
    const order = [...data];
    setOrder(order);
  };

  useEffect(() => {
    resetForm();
  }, [data]);

  const items = order.map((item, index) => item.identifier || index.toString());

  return (
    <Modal
      {...restProps}
      forceRender
      okText="update"
      okButtonProps={{ disabled: !canSave }}
      cancelText="cancel"
      className="approach-category-modal projecthome-sort-modal"
      closeIcon={<span className="appkiticon icon-close-fill" />}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={loading}
    >
      <h3 className="modal-title">Customize Category</h3>
      <div className="header">
        <span className="category-name">Category Name</span>
        <Button className="add-btn" type="link" onClick={handleAdd}>
          <span className="appkiticon icon-plus -fill" />
          Add
        </Button>
      </div>
      <div ref={containerRef} className="draggable-container">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <SortableList
              items={order}
              handleInput={handleInput}
              handleRemove={handleRemove}
            />
          </SortableContext>
        </DndContext>
      </div>
    </Modal>
  );
};

export default memo(CategoryModal);
